// AI employees as a team (每日看板 → 我的團隊, 2026-11-04).
//
// Magnus: the employee threads were "just a list … as if they are real employees", then "each
// employee has its own kpi and they think of new ideas to improve it … I can't just zoom in
// directly". Each of the 13 employees (workspace config/employees/roster.json) gets a staff card —
// status, what they are on or waiting for, their lead KPI against its target with the trend, how
// many of their KPIs are on target and how many improvement ideas they have open — grouped by team
// under 總管. Tapping a card opens the employee's own page (/owner-board/team/:key) with everything.
//
// TeamWaitingStrip is the one-line version for 今日重點: who is waiting on him, one tap to their page.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, LinearProgress, Paper, Typography } from '@mui/material';
import api from '../../services/api';
import { errMessage } from './agentShared';
import {
  AMBER,
  Avatar,
  BAD,
  Employee,
  GOOD,
  Sparkline,
  StatusPill,
  Status,
  autonomyOf,
  deltaTone,
  openIdeas,
  scoreOf,
  statusOf,
  targetText,
  taskNeedsYou,
  withUnit,
} from './employeeShared';
import { AssignBox } from './EmployeeTasks';

const openTasksOf = (e: Employee) => (e.tasks || []).filter((t) => !t.archived);
const waitCount = (e: Employee) => e.approvals.length + openTasksOf(e).filter(taskNeedsYou).length;

// 派工 from the team page: pick who, write the one thing, it starts at once.
const TeamAssign: React.FC<{ employees: Employee[]; onAssigned: () => void }> = ({ employees, onAssigned }) => {
  const [who, setWho] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const picked = employees.find((e) => e.key === who);
  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 3, mb: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1f5b4c' }}>派工 · 選一個人，寫一件事</Typography>
      <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', py: 1 }}>
        {employees.map((e) => (
          <Box key={e.key} role="button" aria-label={`派給${e.name}`} aria-pressed={who === e.key}
            onClick={() => setWho(who === e.key ? null : e.key)}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', flexShrink: 0, width: 46,
              opacity: who && who !== e.key ? 0.45 : 1 }}>
            <Box sx={{ borderRadius: '50%', outline: who === e.key ? '2px solid #1f5b4c' : 'none', outlineOffset: 2 }}>
              <Avatar name={e.name} workstream={e.workstream} size={32} />
            </Box>
            <Typography variant="caption" noWrap sx={{ fontSize: 10.5, maxWidth: 46, fontWeight: who === e.key ? 700 : 400 }}>
              {e.name}
            </Typography>
          </Box>
        ))}
      </Box>
      <AssignBox
        empKey={who}
        name={picked?.name}
        placeholder={picked ? undefined : '先點上面一個人'}
        onAssigned={(text) => { setMsg({ ok: true, text }); onAssigned(); }}
        onError={(text) => setMsg({ ok: false, text })}
      />
      {msg && (
        <Alert severity={msg.ok ? 'success' : 'error'} onClose={() => setMsg(null)} sx={{ mt: 1, py: 0 }}>{msg.text}</Alert>
      )}
    </Paper>
  );
};

const TEAM_ORDER = ['front', 'revenue', 'ops'];
const TEAM_FALLBACK: Record<string, string> = {
  front: '前台 · 讓人買',
  revenue: '營收 · 帶人來、讓人回來',
  ops: '營運 · 讓事情不出錯',
};

export const useTeam = (pollMs = 30000) => {
  const [employees, setEmployees] = useState<Employee[] | null>(null);
  const [teams, setTeams] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/agent-threads/team');
      setEmployees(res.data.employees || []);
      setTeams(res.data.teams || {});
    } catch (err: any) {
      if (err?.response?.status !== 404) setError(errMessage(err));
      setEmployees([]);
    }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
  }, [load, pollMs]);
  return { employees, teams, error, load };
};

// The one line under the name: what they are on, or what they are waiting on him for.
const lineOf = (e: Employee, st: Status): string => {
  if (e.approvals.length) return `要你批：${e.approvals[0].meta.summary || e.approvals[0].content}`;
  const tasks = openTasksOf(e);
  const waiting = tasks.find(taskNeedsYou);
  if (waiting) return `任務等你：${waiting.ask.split('\n')[0]}`;
  const busy = tasks.find((t) => t.state === 'running' || t.state === 'queued');
  if (st.label === '做任務中' && busy) return `做任務：${busy.ask.split('\n')[0]}`;
  if (st.tone === 'work') return e.thread?.status_line || '工作中…';
  const lines = (e.report?.content || '').split('\n').map((l) => l.trim()).filter(Boolean);
  const at = lines.findIndex((l) => /^[-*]?\s*(\*\*)?做了/.test(l));
  if (at >= 0) {
    let did = lines[at].replace(/^[\s\-*]*(\*\*)?做了(\*\*)?[：:]?\s*/, '').replace(/\*\*/g, '');
    // 「做了：」 alone on its line → the work is on the next line(s)
    if (!did && lines[at + 1]) did = lines[at + 1].replace(/^[-*·]\s*/, '').replace(/\*\*/g, '');
    if (did) return `做了：${did}`;
  }
  return e.snapshot?.in_progress?.[0] || '還沒有日報';
};

const LeadKpi: React.FC<{ e: Employee }> = ({ e }) => {
  const k = e.snapshot?.scoreboard?.find((x) => x.value != null);
  if (!k) {
    const legacy = e.snapshot?.kpis?.[0];
    return legacy ? (
      <Typography variant="caption" color="text.secondary" noWrap component="div">
        {legacy.label.slice(0, 22)} <b>{withUnit(legacy.value, '')}</b>
      </Typography>
    ) : null;
  }
  const tone = deltaTone(k);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" noWrap component="div">{k.label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 20, lineHeight: 1.2, color: k.state === 'miss' ? AMBER : 'text.primary' }}>
            {withUnit(k.value, k.unit)}
          </Typography>
          {tone !== 0 && (
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, color: tone > 0 ? GOOD : BAD }}>
              {k.value! > k.prev! ? '▲' : '▼'}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.disabled" noWrap component="div" sx={{ fontSize: 11 }}>
          {targetText(k)}
        </Typography>
      </Box>
      <Box sx={{ width: 64, flexShrink: 0, pb: 0.5 }}>
        <Sparkline k={k} height={26} />
      </Box>
    </Box>
  );
};

const EmployeeCard: React.FC<{ e: Employee; onOpen: () => void }> = ({ e, onOpen }) => {
  const st = statusOf(e);
  const a = autonomyOf(e.snapshot);
  const sc = scoreOf(e.snapshot);
  const ideas = openIdeas(e.snapshot).length;
  return (
    <Paper
      variant="outlined"
      onClick={onOpen}
      role="link"
      aria-label={e.name}
      sx={{ p: 1.5, borderRadius: 3, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0,
        borderWidth: st.tone === 'wait' ? 2 : 1, borderColor: st.tone === 'wait' ? '#EF9F27' : 'divider',
        '&:hover': { borderColor: st.tone === 'wait' ? '#BA7517' : 'text.disabled' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Avatar name={e.name} workstream={e.workstream} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>{e.name}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {e.snapshot?.role || e.workstream}
          </Typography>
        </Box>
        <StatusPill st={st} />
      </Box>
      <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical', overflow: 'hidden', color: st.tone === 'wait' ? 'text.primary' : 'text.secondary' }}>
        {lineOf(e, st)}
      </Typography>
      <LeadKpi e={e} />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', fontSize: 11, color: 'text.secondary' }}>
        {sc.total > 0 && (
          <Box component="span" sx={{ fontWeight: 700, color: sc.hit === sc.total ? GOOD : 'text.secondary' }}>
            達標 {sc.hit}/{sc.total}
          </Box>
        )}
        <Box component="span" sx={{ color: ideas ? '#0C447C' : 'text.disabled', fontWeight: ideas ? 700 : 400 }}>
          點子 {ideas}
        </Box>
        {openTasksOf(e).length > 0 && (
          <Box component="span" sx={{ fontWeight: 700, color: openTasksOf(e).some(taskNeedsYou) ? AMBER : '#0C447C' }}>
            任務 {openTasksOf(e).length}
          </Box>
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={a.total ? (a.auto / a.total) * 100 : 0}
        sx={{ height: 4, borderRadius: 2, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: GOOD } }}
      />
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
        自主 {a.auto} / {a.total} 類
        {a.ready > 0 && (
          <Box component="span" sx={{ color: GOOD, fontWeight: 700 }}> · {a.ready} 類可授權</Box>
        )}
      </Typography>
    </Paper>
  );
};

const EmployeeTeam: React.FC<{ onChanged?: () => void }> = () => {
  const navigate = useNavigate();
  const { employees, teams, error, load } = useTeam();

  const summary = useMemo(() => {
    const list = employees || [];
    const sts = list.map(statusOf);
    const auto = list.reduce((n, e) => n + autonomyOf(e.snapshot).auto, 0);
    const total = list.reduce((n, e) => n + autonomyOf(e.snapshot).total, 0);
    const hit = list.reduce((n, e) => n + scoreOf(e.snapshot).hit, 0);
    const kpis = list.reduce((n, e) => n + scoreOf(e.snapshot).total, 0);
    return {
      working: sts.filter((s) => s.tone === 'work').length,
      waiting: list.reduce((n, e) => n + waitCount(e), 0),
      hit,
      kpis,
      ideas: list.reduce((n, e) => n + openIdeas(e.snapshot).length, 0),
      auto,
      total,
    };
  }, [employees]);

  if (!employees || employees.length === 0) return null;
  const open = (key: string) => navigate(`/owner-board/team/${key}`);
  const chief = employees.find((e) => e.key === 'chief');
  const byTeam = (team: string) => employees.filter((e) => e.key !== 'chief' && (e.snapshot?.team || 'ops') === team);

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#1f5b4c', mb: 1 }}>我的團隊 · {employees.length} 人</Typography>
      {error && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1, mb: 1.5 }}>
        {[
          ['等你', String(summary.waiting), summary.waiting ? AMBER : undefined],
          ['KPI 達標', `${summary.hit} / ${summary.kpis}`, undefined],
          ['點子在跑', String(summary.ideas), undefined],
          ['已授權', `${summary.auto} / ${summary.total} 類`, undefined],
        ].map(([label, value, color]) => (
          <Box key={label} sx={{ bgcolor: 'action.hover', borderRadius: 2, px: 1.5, py: 1 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 22, color: color || 'text.primary', lineHeight: 1.3 }}>{value}</Typography>
          </Box>
        ))}
      </Box>

      <TeamAssign employees={employees} onAssigned={load} />

      {chief && (
        <Paper
          variant="outlined"
          onClick={() => open('chief')}
          role="link"
          aria-label={chief.name}
          sx={{ p: 1.5, borderRadius: 3, cursor: 'pointer', display: 'flex', gap: 1.25, alignItems: 'center', mb: 1,
            borderWidth: chief.approvals.length ? 2 : 1, borderColor: chief.approvals.length ? '#EF9F27' : 'divider' }}
        >
          <Avatar name={chief.name} workstream={chief.workstream} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
              總管{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                {chief.snapshot?.role}
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }} noWrap>
              {lineOf(chief, statusOf(chief))}
            </Typography>
          </Box>
          <StatusPill st={statusOf(chief)} />
        </Paper>
      )}

      {TEAM_ORDER.map((team) => {
        const members = byTeam(team);
        if (!members.length) return null;
        return (
          <Box key={team} sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              {teams[team] || TEAM_FALLBACK[team] || team}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 1 }}>
              {members.map((e) => (
                <EmployeeCard key={e.key} e={e} onOpen={() => open(e.key)} />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

/** 今日重點: who is waiting on Magnus, each one tap away from their page. */
export const TeamWaitingStrip: React.FC = () => {
  const navigate = useNavigate();
  const { employees } = useTeam(60000);
  const waiting = (employees || []).filter((e) => waitCount(e));
  if (!waiting.length) return null;
  const cards = waiting.reduce((n, e) => n + waitCount(e), 0);
  return (
    <Paper variant="outlined" sx={{ p: 1.25, mb: 2, borderRadius: 3, borderColor: '#EF9F27' }}>
      <Typography variant="caption" sx={{ color: AMBER, fontWeight: 700 }}>團隊等你 · {cards} 件</Typography>
      <Box sx={{ display: 'flex', gap: 1.25, overflowX: 'auto', pt: 1, pb: 0.25 }}>
        {waiting.map((e) => (
          <Box
            key={e.key}
            role="link"
            aria-label={`${e.name} 等你`}
            onClick={() => navigate(`/owner-board/team/${e.key}`)}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, cursor: 'pointer', flexShrink: 0,
              width: 56, position: 'relative' }}
          >
            <Avatar name={e.name} workstream={e.workstream} size={40} />
            <Box sx={{ position: 'absolute', top: -4, right: 4, bgcolor: '#EF9F27', color: '#fff', fontSize: 10, fontWeight: 700,
              borderRadius: 99, minWidth: 16, height: 16, px: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {waitCount(e)}
            </Box>
            <Typography variant="caption" noWrap sx={{ fontSize: 11, maxWidth: 56 }}>{e.name}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default EmployeeTeam;
